/**
 * TREATX-P0-ONBOARDING-STATE-MACHINE-REDESIGN
 * =============================================
 * Client-side helper for the stateful onboarding state machine.
 *
 *   /api/onboarding/registration-sessions           (start | restart, idempotent)
 *   /api/onboarding/registration-sessions/{id}      (poll)
 *   /api/onboarding/registration-sessions/{id}/complete
 *   /api/onboarding/verify-email
 *   /api/onboarding/resume-check?email=…
 *
 * The `(session_id, session_secret)` pair is authoritative and lives in
 * localStorage until the session reaches `completed`. Every request
 * carries the secret via the `X-Session-Secret` header — the backend
 * validates via constant-time comparison against a stored hash.
 *
 * Handles the SoftLaunchGate fetch interceptor automatically: it
 * already stamps `X-Soft-Launch-Session` on every window.fetch, so we
 * can call raw endpoints without re-wiring headers.
 */

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const LS_KEY_PREFIX = 'treatx.onboarding.registrationSession';
// BUG-4 — post-verification resume anchor. The registration session is
// consumed (and cleared) by /complete, after which the wizard previously
// held the application_id and the current step in React memory ONLY: any
// remount or refresh dropped the provider into an empty wizard even though
// a real application already existed. This anchor stores nothing but the
// canonical application_id the server already issued plus the step the
// provider reached. The BACKEND application remains the sole authority —
// this is the same client-side resume anchor as the pre-verify envelope,
// in the same storage namespace (NOT a second application-state authority).
const LS_PROGRESS_PREFIX = 'treatx.onboarding.postVerifyProgress';

// One live session per (role) — a provider can't be mid-signup as
// two providers in the same browser at once.
const storageKey = (role) => `${LS_KEY_PREFIX}.${role}`;
const progressKey = (role) => `${LS_PROGRESS_PREFIX}.${role}`;

export const loadOnboardingProgress = (role) => {
  try {
    const raw = localStorage.getItem(progressKey(role));
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || !parsed.application_id) return null;
    return parsed;
  } catch { return null; }
};

export const persistOnboardingProgress = (role, progress) => {
  try {
    localStorage.setItem(progressKey(role), JSON.stringify(progress));
  } catch { /* ignore quota */ }
};

export const clearOnboardingProgress = (role) => {
  try {
    localStorage.removeItem(progressKey(role));
  } catch { /* ignore */ }
};

export const loadStoredSession = (role) => {
  try {
    const raw = localStorage.getItem(storageKey(role));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

export const persistSession = (role, session) => {
  try {
    localStorage.setItem(storageKey(role), JSON.stringify(session));
  } catch { /* ignore quota */ }
};

export const clearStoredSession = (role) => {
  try {
    localStorage.removeItem(storageKey(role));
  } catch { /* ignore */ }
};

const jsonOrThrow = async (r) => {
  const body = await r.json().catch(() => ({}));
  if (!r.ok) {
    // FastAPI HTTPException.detail may be a plain string OR a
    // structured list (validation errors). Never store an object
    // where downstream code will render it — React will bail out
    // and print "[object Object]".
    const rawDetail = body?.detail ?? body?.message;
    const detailStr = typeof rawDetail === 'string' ? rawDetail : JSON.stringify(rawDetail || {});
    const err = new Error(detailStr || `HTTP ${r.status}`);
    err.status = r.status;
    err.detail = detailStr;
    err.body = body;
    throw err;
  }
  return body;
};

export const startRegistrationSession = async ({ role, email, password, formData }) => {
  const r = await fetch(`${API}/api/onboarding/registration-sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      role,
      email: (email || '').trim().toLowerCase(),
      password,
      form_data: formData || {},
    }),
  });
  return jsonOrThrow(r);
};

export const pollSessionStatus = async ({ sessionId, sessionSecret }) => {
  const r = await fetch(`${API}/api/onboarding/registration-sessions/${sessionId}`, {
    method: 'GET',
    headers: { 'X-Session-Secret': sessionSecret },
  });
  return jsonOrThrow(r);
};

export const completeSession = async ({ sessionId, sessionSecret }) => {
  const r = await fetch(`${API}/api/onboarding/registration-sessions/${sessionId}/complete`, {
    method: 'POST',
    // credentials:'include' so the application-bound status_session +
    // treatx_csrf cookies minted by /complete are stored in THIS browser,
    // which then continues onboarding (requirements/upload/submit) authenticated.
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Session-Secret': sessionSecret },
    body: JSON.stringify({}),
  });
  return jsonOrThrow(r);
};

export const verifyEmailToken = async (token) => {
  const r = await fetch(`${API}/api/onboarding/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  return jsonOrThrow(r);
};

export const resumeCheck = async (email) => {
  const r = await fetch(`${API}/api/onboarding/resume-check?email=${encodeURIComponent(email)}`, {
    method: 'GET',
  });
  return jsonOrThrow(r);
};

// TREATX-P0-ONBOARDING-UNIFICATION-01: server-of-truth resume.
// The browser cache is optional; recovery works from ANY browser as
// long as the user knows (email, password). Returns the redacted
// session envelope + a freshly rotated `session_secret`.
export const resumeByCredentials = async ({ email, password }) => {
  const r = await fetch(`${API}/api/onboarding/resume`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: (email || '').trim().toLowerCase(), password }),
  });
  return jsonOrThrow(r);
};
